import { useState } from 'react';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation } from '@tanstack/react-query';
import { Button, Field, Input } from '@promanage/ui';
import { AuthSplit } from '../features/auth/AuthSplit';
import { PasswordField } from '../features/auth/PasswordField';
import { MFA_PATH, safeRedirect } from '../features/auth/gate';
import { resetClientCache } from '../features/auth/session';
import { PRIVACY_URL, TERMS_URL } from '../lib/legal';
import { trpc } from '../lib/trpc';

export const Route = createFileRoute('/_auth/login')({
  // The key is OMITTED rather than set to undefined when absent, so `<Link to="/login">`
  // stays valid without one. Kept as an unvalidated string and sanitised at the point of
  // USE, by `safeRedirect`: rejecting it here would turn a hostile link into a router error
  // page instead of a quiet fallback to the dashboard.
  validateSearch: (search: Record<string, unknown>): { redirect?: string } =>
    typeof search.redirect === 'string' ? { redirect: search.redirect } : {},
  component: Login,
});

function Login() {
  const navigate = useNavigate();
  const search = Route.useSearch();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const login = useMutation(
    trpc.auth.login.mutationOptions({
      meta: { errorTitle: 'Could not sign in', credentials: true },
      onSuccess(result) {
        // One login may belong to several agencies, and this page neither asks which nor learns
        // of the others: the server opens the person's default, and the switcher inside the
        // console — behind the second factor — is where the rest are.
        //
        // A session now exists, and it is a different identity from whatever this browser was
        // holding a moment ago. Drop every cached query before navigating.
        resetClientCache();

        if (result.status === 'active') {
          void navigate({ to: safeRedirect(search.redirect), replace: true });
          return;
        }

        // `mfa_required` and `mfa_enrolment_required` both land here; the MFA page reads the
        // session and works out which of the two it is dealing with.
        void navigate({ to: MFA_PATH, replace: true });
      },
    }),
  );

  const submit = (event: React.FormEvent) => {
    event.preventDefault();
    login.mutate({ email: email.trim(), password });
  };

  return (
    <AuthSplit
      title="Sign in"
      heading="Welcome back"
      intro="Sign in to your ProManage account."
      footer={
        <>
          <p>
            ProManage holds money on behalf of other people. Never enter these details on a page you
            did not reach yourself.
          </p>
          <p className="mt-3 flex justify-center gap-4">
            <a href={PRIVACY_URL} className="hover:underline">
              Privacy policy
            </a>
            <a href={TERMS_URL} className="hover:underline">
              Terms of use
            </a>
          </p>
        </>
      }
    >
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label="Email" required>
          {(props) => (
            <Input
              {...props}
              type="email"
              autoComplete="username"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>

        <PasswordField value={password} onChange={setPassword} autoComplete="current-password" />

        <Button type="submit" size="lg" disabled={login.isPending} className="mt-1">
          {login.isPending ? 'Signing in' : 'Sign in'}
        </Button>
      </form>

      <p className="mt-5 text-center text-[0.8125rem] text-ink-muted">
        New to ProManage?{' '}
        <Link to="/signup" className="font-medium text-accent hover:underline">
          Sign up
        </Link>
      </p>
    </AuthSplit>
  );
}
